/**
* @summary - Calibration Certificate List
* @author Yuvasri S
* @since 2024-12-17
*/

import React, { useState, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CustomDialog from '../commoncomponents/CustomDialog';
import { useNavigate } from 'react-router-dom';
import { DefaultCard } from '../../assets/styled-components/card';
import CreateNewButton from '../commoncomponents/CreateNewButton';
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import CloseButton from '../commoncomponents/CloseButton';
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import { dialogContent } from '../../utility/message';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import calibrationCertificateRepository from '../../api/repositories/calibrationCertificateRepository';

function CalibrationCertificateList() {

  const [rows, setRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [filteredRows, setFilteredRows] = useState([]);
  const [rowToDelete, setRowToDelete] = useState(null);
  const navigate = useNavigate();
  const [content, setContent] = useState();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);

  const serviceOptions1 = [
    { label: "FEMS", value: "FEMS", id: 1 },
    { label: "BEMS", value: "BEMS", id: 2 },
  ];
  const [service1, setService1] = useState(serviceOptions1[0]);

  const transformedRows = filteredRows.map(row => ({
    ...row,
    calibexpirydateformat: row.calibexpirydate
      ? new Date(row.calibexpirydate).toLocaleDateString('en-GB')
      : 'N/A',

    isactiveformate: row.isactive ? 'Active' : 'Inactive',
  }));

  const columns = [
    { field: 'caliblicenseno', headerName: 'Calibration No', width: 150 },
    { field: 'categoryName', headerName: 'Calibration Category', width: 150 },
    { field: 'licntypecode', headerName: 'Calibration Type Code', width: 150 },
    { field: 'licntypevalue', headerName: 'Callibration Type Code Description', width: 150 },
    { field: 'calibclassgrade', headerName: 'Class/Grade', width: 150 },
    {
      field: 'calibexpirydateformat', headerName: 'Expiry Date', width: 150, filter: false,
      sort: false
    },
    { field: 'isactiveformate', headerName: 'Status', width: 150 },
  ];

  const fetchCalibrationCertificate = async () => {
    setIsLoading(true);
    try {
      const response = await calibrationCertificateRepository.calibrationCertificateList(service1.value, pageIndex, pageSize, filterModel, sortModel);
      setRowCount(response.data.totalCount);
      setRows(response.data.items);
      setFilteredRows(response.data.items);
    } catch (error) {
      // Handle errors
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExportdata = async () => {
    setIsLoading(true);
    try {
      const response = await calibrationCertificateRepository.calibrationCertificateExport(
        service1.value,
        filterModel,
        sortModel
      );

      // Format the dates before setting the export data
      const formattedData = response.data.map((row) => ({
        ...row,
        calibexpirydateformat: row.calibexpirydate
          ? new Date(row.calibexpirydate).toLocaleDateString('en-GB')
          : 'N/A',
      }));

      setExportData(formattedData);
    } catch (error) {
      console.error("Error fetching export data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCalibrateCertificate = async (id) => {
    try {
      const response = await calibrationCertificateRepository.deleteCalibrateCertificate(id);
      if (response.status === 204) {
        setConfirmationDialogOpen(true)
        setContent('Record deleted successfully.')
      }
      fetchCalibrationCertificate();
    }
    catch (error) {

      if (error.response && error.response.status === 400) {
        setConfirmationDialogOpen(true);

        setContent(error ? error.response.data.detail : "Error Occured!")
      }
    }
  };

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };

  const handleServiceChange1 = (newValue) => {
    setService1(newValue);
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([]);
    setSortModel([]);
  };
  const handleCreateLicenseCertificate = () => {
    navigate("register", { state: { title: 'Create Calibration Certificate Details' } });
  };

  const handleEdit = (rowData) => {
    navigate("register", { state: { ...rowData, editmode: true, title: 'Edit Calibration Certificate Details' } });
  };

  const handleView = (rowData) => {
    navigate("register", { state: { ...rowData, viewmode: true, title: 'View Calibration Certificate Details' } });
  };
  useEffect(() => {
    fetchCalibrationCertificate();
  }, [filterModel, sortModel, pageIndex, pageSize]);

  const confirmDelete = async () => {
    if (rowToDelete) {
      await deleteCalibrateCertificate(rowToDelete.id);
      setDeleteDialogOpen(false);
      setConfirmationDialogOpen(true);
      setRowToDelete(null);
    }
  };
  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <PageTitle title="Calibration Certificate Details" />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="Calibration Certificate Details List" />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomSelectInput
              fullWidth={false}
              labelSize={"14px"}
              sx={{ width: 150 }}
              label={"Service"}
              options={serviceOptions1}
              value={service1}
              onChange={handleServiceChange1}
              placeholder={"Select Service"}
              highlight={true}
            />
            <CreateNewButton onClick={handleCreateLicenseCertificate} />
            <RefreshExportButtons
              export={filteredRows}
              exportFunction={fetchExportdata}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title="Calibration Certificate Details"
            />
          </Stack>
        </Box>
        <CustomAgGrid
          rows={transformedRows}
          columns={columns}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleView={handleView}
          setGridApi={setGridApi}
          isLoading={isLoading}
          ref={gridApi}
          pagination={false}
          setFilterModel={setFilterModel}
          setSortModel={setSortModel}
          totalCount={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />
        <CustomDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}

          onConfirm={confirmDelete}
          content={dialogContent.confirmDelete}
          cancelBtn='No'
          confirmBtn="Yes"
        />
        <CustomDialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
          content={
            content
          }
          confirmBtn="OK"
          onConfirm={() => setConfirmationDialogOpen(false)}
        />

      </DefaultCard>
      <CloseButton />

    </>
  );
}

export default CalibrationCertificateList;






